/**
 * Per-tenant content-module configuration shapes + merge helpers.
 *
 * <p>Every system layout consumes a module config (blog, wiki, video,
 * micro) — operator-authored, stored on the tenant's shell config,
 * passed into the layout as a prop. Layouts ALWAYS read merged
 * config via these helpers so every behavioural field has a concrete
 * value at the call site.
 *
 * <p>Adding a new field is a three-step change:
 * <ol>
 *   <li>Extend the module config type below.</li>
 *   <li>Add a default in the corresponding {@code *_DEFAULTS}.</li>
 *   <li>Read it from the layout that needs it.</li>
 * </ol>
 *
 * <p>Direct port of ThemeMaker's
 * {@code lib/platform/contentModuleConfig.ts}.
 */

import type { BlockInstance, LocalizedString } from './types';
import type { ArticleBlock } from './types';

export type ChromeMode = 'full' | 'minimal' | 'immersive';
export type CategoriesPlacement = 'sidebar' | 'rail-top' | 'shell-strip' | 'inline' | 'none';

/** Where a module's search affordance renders. */
export interface ModuleSearchConfig {
  placement: 'header' | 'body' | 'both' | 'none';
}

/** Per-tenant article-detail page template. Wraps a list of
 *  block-instances scoped to article placement zones. */
export interface ArticleTemplate {
  blocks?: ArticleBlock[];
}

// =============================================================================
// Module config shapes
// =============================================================================

export interface BlogModuleConfig {
  shellKey?: string | null;
  chromeMode?: ChromeMode;
  categoriesPlacement?: CategoriesPlacement;
  categoriesTitle?: LocalizedString;
  categoriesMenuOverride?: 'module' | 'default';
  search?: ModuleSearchConfig;
  showFeatured?: boolean;
  pageSize?: number;
  showAuthor?: boolean;
  showReadTime?: boolean;
  showRelated?: boolean;
  articleCardVariant?: 'image-led' | 'minimal' | 'magazine';
  blogHeroVariant?: 'gradient' | 'cover-photo' | 'split' | 'serif-split' | 'none';
  title?: LocalizedString;
  subtitle?: LocalizedString;
  description?: LocalizedString;
  aboveBlocks?: BlockInstance[];
  belowBlocks?: BlockInstance[];
  articleTemplate?: ArticleTemplate;
}

/**
 * News module config — {@code shell.config.contentModule.news}. News is an
 * article-shaped content module (landing + entity detail) framed by its own
 * {@code shellKey} (or the org default when unset). Leaner than blog: no
 * card-variant/hero knobs yet — just shell wrap + landing header + page size.
 */
export interface NewsModuleConfig {
  shellKey?: string | null;
  chromeMode?: ChromeMode;
  search?: ModuleSearchConfig;
  pageSize?: number;
  showFeatured?: boolean;
  title?: LocalizedString;
  subtitle?: LocalizedString;
  description?: LocalizedString;
}

export interface WikiModuleConfig {
  shellKey?: string | null;
  chromeMode?: ChromeMode;
  search?: ModuleSearchConfig;
  showToc?: boolean;
  showBreadcrumbs?: boolean;
  showPrevNext?: boolean;
  expandTreeToDepth?: number;
  showLastUpdated?: boolean;
  wikiTreeVariant?: 'standard' | 'stripe' | 'minimal';
  wikiDetailVariant?: 'standard' | 'narrow' | 'split';
  title?: LocalizedString;
  subtitle?: LocalizedString;
  description?: LocalizedString;
  aboveBlocks?: BlockInstance[];
  belowBlocks?: BlockInstance[];
  pageTemplate?: ArticleTemplate;
}

/**
 * Canonical micro config — the ADMIN shape (deliberately thin after the
 * Phase-4/round-2 deletions documented in the admin's platform types;
 * title/subtitle/description/pageSize were removed with their consumers).
 * Keep this and the admin type field-identical: no dual shapes.
 *
 * <p>Per-kind display names / visibility are NOT here: they live on the
 * backend {@code MicroContentType} entity, edited on the admin's
 * "Content types" page and served merged through
 * {@code MicroSource.types.list()}. A {@code contentTypeOverrides} copy of
 * that table used to sit on this config; nothing ever read it.
 */
export interface MicroModuleConfig {
  shellKey?: string | null;
  /** Only {@code 'shell-strip'} is meaningful for micro (header chip
   *  strip — not yet implemented in the port); other values are no-ops. */
  categoriesPlacement?: CategoriesPlacement;
  categoriesMenuOverride?: 'module' | 'default';
  /** Admin wire shape: both fields optional, placement defaults to
   *  {@code 'header'} at resolve time (same contract the blog/wiki/video
   *  forms author). */
  search?: { placement?: ModuleSearchConfig['placement']; placeholder?: string };
}

export interface VideoModuleConfig {
  shellKey?: string | null;
  chromeMode?: ChromeMode;
  heroVariant?: 'rail' | 'cinematic' | 'single' | 'none';
  defaultSource?: string;
  pageSize?: number;
  search?: ModuleSearchConfig;
  categoriesPlacement?: CategoriesPlacement;
  videoCardVariant?: 'youtube' | 'minimal' | 'poster' | 'cinematic';
  showDirector?: boolean;
  showRelated?: boolean;
  showDescription?: boolean;
  showEpisodeNumbers?: boolean;
  showChannels?: boolean;
  channelCardVariant?: 'detailed' | 'compact' | 'avatar-only';
  defaultDiscoverSort?: 'top-subscribed' | 'newest' | 'alphabetical';
  showHistory?: boolean;
  showWatchLater?: boolean;
  showSubscriptions?: boolean;
  title?: LocalizedString;
  subtitle?: LocalizedString;
  description?: LocalizedString;
  aboveBlocks?: BlockInstance[];
  belowBlocks?: BlockInstance[];
}

// =============================================================================
// Defaults
// =============================================================================

export const BLOG_DEFAULTS = {
  chromeMode: 'full' as const,
  categoriesPlacement: 'sidebar' as const,
  search: { placement: 'header' as const },
  showFeatured: true,
  pageSize: 12,
  showAuthor: true,
  showReadTime: true,
  showRelated: true,
  articleCardVariant: 'image-led' as const,
  blogHeroVariant: 'gradient' as const,
} as const;

export const WIKI_DEFAULTS = {
  chromeMode: 'full' as const,
  search: { placement: 'header' as const },
  showToc: true,
  showBreadcrumbs: true,
  showPrevNext: true,
  expandTreeToDepth: 1,
  showLastUpdated: true,
  wikiTreeVariant: 'standard' as const,
  wikiDetailVariant: 'standard' as const,
} as const;

/** Micro defaults describe what the kind listing drew before the config
 *  was consumed at all — pills above the grid and an in-body search box —
 *  so a tenant that never opened the Configuration page sees no change. */
export const MICRO_DEFAULTS = {
  categoriesPlacement: 'rail-top' as const,
  search: { placement: 'body' as const },
} as const;

export const VIDEO_DEFAULTS = {
  chromeMode: 'full' as const,
  heroVariant: 'rail' as const,
  defaultSource: 'trending' as const,
  pageSize: 24,
  search: { placement: 'header' as const },
  categoriesPlacement: 'inline' as const,
  videoCardVariant: 'youtube' as const,
  showDirector: true,
  showRelated: true,
  showDescription: true,
  showEpisodeNumbers: true,
  showChannels: true,
  channelCardVariant: 'detailed' as const,
  defaultDiscoverSort: 'top-subscribed' as const,
  showHistory: true,
  showWatchLater: true,
  showSubscriptions: true,
} as const;

// =============================================================================
// Resolved (defaulted) types — what layouts see after merge
// =============================================================================

type DefaultedKeys<T, D> = Extract<keyof T, keyof D>;
type WithDefaults<T, D> = T & Required<Pick<T, DefaultedKeys<T, D>>>;

export type ResolvedBlogConfig = WithDefaults<BlogModuleConfig, typeof BLOG_DEFAULTS>;
export type ResolvedWikiConfig = WithDefaults<WikiModuleConfig, typeof WIKI_DEFAULTS>;
export type ResolvedMicroConfig = WithDefaults<MicroModuleConfig, typeof MICRO_DEFAULTS>;
export type ResolvedVideoConfig = WithDefaults<VideoModuleConfig, typeof VIDEO_DEFAULTS>;

// =============================================================================
// Merge helpers
// =============================================================================

export function mergeBlogConfig(cfg: BlogModuleConfig | undefined): ResolvedBlogConfig {
  return { ...BLOG_DEFAULTS, ...(cfg ?? {}) };
}

export function mergeWikiConfig(cfg: WikiModuleConfig | undefined): ResolvedWikiConfig {
  return { ...WIKI_DEFAULTS, ...(cfg ?? {}) };
}

export function mergeMicroConfig(cfg: MicroModuleConfig | undefined): ResolvedMicroConfig {
  return { ...MICRO_DEFAULTS, ...(cfg ?? {}) };
}

export function mergeVideoConfig(cfg: VideoModuleConfig | undefined): ResolvedVideoConfig {
  return { ...VIDEO_DEFAULTS, ...(cfg ?? {}) };
}

// =============================================================================
// Search-placement resolvers
// =============================================================================
//
// Modern explicit `search.placement` wins; otherwise the module default.
// The legacy `showSearch` fallback was deleted alongside the deprecated
// field (per [[no_legacy_compat]]: port to the new path, delete the old).

export function resolveVideoSearchPlacement(
  cfg: VideoModuleConfig | undefined,
): 'header' | 'body' | 'both' | 'none' {
  return cfg?.search?.placement ?? VIDEO_DEFAULTS.search.placement;
}

export function resolveBlogSearchPlacement(
  cfg: BlogModuleConfig | undefined,
): 'header' | 'body' | 'both' | 'none' {
  return cfg?.search?.placement ?? BLOG_DEFAULTS.search.placement;
}

export function resolveWikiSearchPlacement(
  cfg: WikiModuleConfig | undefined,
): 'header' | 'body' | 'both' | 'none' {
  return cfg?.search?.placement ?? WIKI_DEFAULTS.search.placement;
}

export function resolveMicroSearchPlacement(
  cfg: MicroModuleConfig | undefined,
): 'header' | 'body' | 'both' | 'none' {
  return cfg?.search?.placement ?? MICRO_DEFAULTS.search.placement;
}

// =============================================================================
// Micro kind-listing chrome
// =============================================================================

export interface MicroListingChrome {
  /** Render the listing's own search box (placement {@code body} / {@code both}). */
  showBodySearch: boolean;
  /** Operator placeholder for that box; undefined → the block's default copy. */
  searchPlaceholder: string | undefined;
  /** Where the category filter renders. Values the listing has no layout
   *  for ({@code shell-strip}, {@code inline}) collapse to the pill rail. */
  categoriesPlacement: 'sidebar' | 'rail-top' | 'none';
}

/** What {@code MicroListingLayout} draws for a micro config. Pure, so the
 *  live route and the admin's studio preview resolve identically. */
export function resolveMicroListingChrome(cfg: MicroModuleConfig | undefined): MicroListingChrome {
  const placement = resolveMicroSearchPlacement(cfg);
  const placeholder = cfg?.search?.placeholder?.trim();
  const categories = cfg?.categoriesPlacement ?? MICRO_DEFAULTS.categoriesPlacement;
  return {
    showBodySearch: placement === 'body' || placement === 'both',
    searchPlaceholder: placeholder ? placeholder : undefined,
    categoriesPlacement:
      categories === 'sidebar' || categories === 'none' ? categories : 'rail-top',
  };
}

// =============================================================================
// Where the config lives on the wire
// =============================================================================
//
// The portal never receives OrgShell.moduleConfig — neither /config/web nor
// /render/shell carries it (verified against a live tenant). What DOES reach
// it is the default shell's slot fills, whose SiteShell block holds
// props.contentModule[module]: the location templates seed per-module config
// into. Until the backend ships moduleConfig, that block is the one place both
// sides agree on — the portal reads it, and the admin Configuration editor
// writes it alongside moduleConfig.

export type ContentModuleKey = 'blog' | 'wiki' | 'video' | 'micro' | 'news' | 'faq' | 'help';

type SlotFills = Record<string, unknown>;

function isSiteShellBlock(
  block: unknown,
): block is { type: 'SiteShell'; props?: Record<string, unknown> } {
  return !!block && typeof block === 'object' && (block as { type?: unknown }).type === 'SiteShell';
}

/** The per-module config stored on the SiteShell block of a slot map, or
 *  undefined when no slot holds a SiteShell block carrying one. */
export function readContentModuleConfig(
  slotFills: SlotFills | null | undefined,
  module: ContentModuleKey,
): Record<string, unknown> | undefined {
  if (!slotFills) return undefined;
  for (const slot of Object.values(slotFills)) {
    if (!Array.isArray(slot)) continue;
    for (const block of slot) {
      if (!isSiteShellBlock(block)) continue;
      const cm = block.props?.['contentModule'];
      const entry =
        cm && typeof cm === 'object' ? (cm as Record<string, unknown>)[module] : undefined;
      if (entry && typeof entry === 'object') return entry as Record<string, unknown>;
    }
  }
  return undefined;
}

/**
 * A copy of {@code slotFills} whose first SiteShell block has
 * {@code props.contentModule[module]} replaced by {@code config}. Returns
 * null when no slot holds a SiteShell block — there is nowhere to write,
 * and inventing one would change the shell's chrome.
 */
export function writeContentModuleConfig<T extends SlotFills>(
  slotFills: T | null | undefined,
  module: ContentModuleKey,
  config: Record<string, unknown>,
): T | null {
  if (!slotFills) return null;
  for (const [slotKey, slot] of Object.entries(slotFills)) {
    if (!Array.isArray(slot)) continue;
    const idx = slot.findIndex(isSiteShellBlock);
    if (idx === -1) continue;
    const block = slot[idx] as { props?: Record<string, unknown> };
    const cm = block.props?.['contentModule'];
    const contentModule = {
      ...(cm && typeof cm === 'object' ? (cm as Record<string, unknown>) : {}),
      [module]: config,
    };
    const nextSlot = [...slot];
    nextSlot[idx] = { ...block, props: { ...(block.props ?? {}), contentModule } };
    return { ...slotFills, [slotKey]: nextSlot } as T;
  }
  return null;
}
